import {showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';

import {PurchasePriceRuleResponseBody, useReorderPurchasePriceRulesMutation} from '@dms/api/sales';
import {SortableCardList, SortableCardListMove} from '@dms/features/dnd';
import {i18n} from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {PurchasePriceRuleCard} from './PurchasePriceRuleCard';
import {PurchasePriceSystemRuleCard} from './PurchasePriceSystemRuleCard';

const CONTAINER_ID = 'purchase-price-rules';
const FIRST_POSITION = 1;

interface PurchasePriceRuleListProps {
  /** All rules sorted by position, the system rule last. */
  rules: PurchasePriceRuleResponseBody[];
  canManage: boolean;
}

export function PurchasePriceRuleList(props: PurchasePriceRuleListProps) {
  const [reorderPurchasePriceRules, {isLoading: isReordering}] =
    useReorderPurchasePriceRulesMutation();

  const sortableRules = props.rules.filter((rule) => !rule.system);
  const systemRules = props.rules.filter((rule) => rule.system);

  const handleReorder = (
    reorderedRules: PurchasePriceRuleResponseBody[],
    move: SortableCardListMove<PurchasePriceRuleResponseBody>
  ) =>
    reorderPurchasePriceRules({
      reorderPurchasePriceRulesRequestBody: {
        items: reorderedRules.map((rule, index) => ({id: rule.id, position: index})),
      },
    })
      .unwrap()
      .then(() =>
        showNotification.success(
          i18n.t('page.settings.purchasePrice.marginRules.reorderedNotification', {
            name: move.item.name,
            position: move.toIndex + FIRST_POSITION,
            total: props.rules.length,
          })
        )
      )
      .catch(handleApiError);

  return (
    <VStack spacing={2}>
      <SortableCardList<PurchasePriceRuleResponseBody>
        withHandle
        disableDropAnimation
        disabledDrag={!props.canManage || isReordering}
        containerId={CONTAINER_ID}
        items={sortableRules}
        onReorder={handleReorder}
        renderCard={(rule, _index, handleProps) => (
          <PurchasePriceRuleCard
            rule={rule}
            rules={props.rules}
            canManage={props.canManage}
            handleProps={props.canManage ? handleProps : undefined}
          />
        )}
      />

      {systemRules.map((rule) => (
        <PurchasePriceSystemRuleCard key={rule.id} rule={rule} canManage={props.canManage} />
      ))}
    </VStack>
  );
}
